import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useMe } from '@/auth/useMe'
import { RichTextEditor } from '@/components/RichTextEditor'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { CategorySelect } from '../components/CategorySelect'
import { OrganisationCombobox } from '../components/OrganisationCombobox'
import { todayIso } from '../dates'
import { CrmApiError } from '../http'
import { shortDay } from '../record/format'
import { clearDraft, readDraft, writeDraft } from '../timeline/drafts'
import type { CrmCategoryRef } from '../types'
import { createAgreement, previewSchedule, updateAgreement } from './api'
import { ContactCombobox } from './ContactCombobox'
import { defaultLeadDays } from './format'
import type { Agreement, AgreementInput, AgreementParty, IntervalUnit, Ref, Schedule, SchedulePreview } from './types'

interface Draft {
  title: string
  category: CrmCategoryRef | null
  description_html: string
  organisation: Ref | null
  person: Ref | null
  owner: string
  starts_on: string
  ends_on: string
  schedule: Schedule
  interval_count: string
  interval_unit: IntervalUnit
  anchor_on: string
  lead_days: string
  /** The lead time was typed: it no longer follows the unit. */
  lead_touched: boolean
  task_title: string
  remind: boolean
  end_reminder_days: string
}

const SCHEDULES: { value: Schedule; label: string }[] = [
  { value: 'geen', label: 'Geen' },
  { value: 'eenmalig', label: 'Eenmalig' },
  { value: 'terugkerend', label: 'Terugkerend' },
  { value: 'contactritme', label: 'Contactritme' },
]

const UNITS: { value: IntervalUnit; label: string }[] = [
  { value: 'week', label: 'week' },
  { value: 'maand', label: 'maand' },
  { value: 'jaar', label: 'jaar' },
]

const PREVIEW_DELAY_MS = 300
const fieldClass = 'h-8 rounded-md border border-border bg-background px-2 text-sm max-md:h-11 max-md:text-base'

function isDraft(value: unknown): value is Draft {
  return typeof value === 'object' && value !== null && typeof (value as Draft).title === 'string' && typeof (value as Draft).schedule === 'string'
}

function fromAgreement(agreement: Agreement): Draft {
  return {
    title: agreement.title,
    category: agreement.category,
    description_html: agreement.description_html ?? '',
    organisation: agreement.organisation,
    person: agreement.person,
    owner: agreement.owner?.id ?? '',
    starts_on: agreement.starts_on,
    ends_on: agreement.ends_on ?? '',
    schedule: agreement.schedule,
    interval_count: agreement.interval_count ? String(agreement.interval_count) : '1',
    interval_unit: agreement.interval_unit ?? 'maand',
    anchor_on: agreement.anchor_on ?? '',
    lead_days: String(agreement.lead_days),
    lead_touched: true,
    task_title: agreement.task_title ?? '',
    remind: agreement.end_reminder_days !== null,
    end_reminder_days: String(agreement.end_reminder_days ?? 30),
  }
}

function emptyDraft(party: AgreementParty | null, owner: string): Draft {
  return {
    title: '',
    category: null,
    description_html: '',
    organisation: party?.kind === 'organisation' ? { id: party.id, name: party.name } : null,
    person: party?.kind === 'contact' ? { id: party.id, name: party.name } : null,
    owner,
    starts_on: todayIso(),
    ends_on: '',
    schedule: 'geen',
    interval_count: '1',
    interval_unit: 'maand',
    anchor_on: '',
    lead_days: '7',
    lead_touched: false,
    task_title: '',
    remind: false,
    end_reminder_days: '30',
  }
}

function toInput(draft: Draft): AgreementInput {
  const rhythm = draft.schedule === 'terugkerend' || draft.schedule === 'contactritme'
  return {
    title: draft.title.trim(),
    category_id: draft.category?.id ?? null,
    description_html: draft.description_html || null,
    organisation_id: draft.organisation?.id ?? null,
    person_id: draft.person?.id ?? null,
    owner_user_id: draft.owner || null,
    starts_on: draft.starts_on || null,
    ends_on: draft.ends_on || null,
    schedule: draft.schedule,
    interval_count: rhythm ? Number(draft.interval_count) || null : null,
    interval_unit: rhythm ? draft.interval_unit : null,
    anchor_on: draft.schedule === 'eenmalig' || draft.schedule === 'terugkerend' ? draft.anchor_on || null : null,
    lead_days: draft.lead_days === '' ? null : Number(draft.lead_days),
    task_title: draft.task_title.trim() || null,
    end_reminder_days: draft.remind && draft.ends_on ? Number(draft.end_reminder_days) || null : null,
  }
}

/**
 * New or edited agreement (spec-63 E4, design §4.6; `Dialog size="lg"`). The next deadlines come from
 * the server (`schedule-preview`), so the dialog uses the planner's arithmetic; its 422 is shown inline.
 * The draft lives in sessionStorage until it is saved or cancelled (ED-52).
 */
export function AgreementDialog({
  open,
  onOpenChange,
  party,
  agreement,
  defaultOwnerId,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The page's relation: fixed. Null on the Afspraken page: choose an organisation and/or a contact. */
  party: AgreementParty | null
  /** Edit this agreement instead of making a new one. */
  agreement?: Agreement
  /** The owner of the relation (ED-24 default); else the person logged in. */
  defaultOwnerId?: string | null
  onSaved: (agreement: Agreement) => void
}) {
  const id = useId()
  const { me } = useMe()
  const { membersById } = useTeamMembers()
  const draftKey = `crm:afspraak:${agreement ? agreement.id : party ? `${party.kind}:${party.id}` : 'nieuw'}`
  const [draft, setDraft] = useState<Draft>(
    () => readDraft(draftKey, isDraft) ?? (agreement ? fromAgreement(agreement) : emptyDraft(party, defaultOwnerId ?? me?.id ?? '')),
  )
  const [preview, setPreview] = useState<SchedulePreview | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const lookup = useRef(0)

  function change(patch: Partial<Draft>) {
    setDraft((current) => {
      const next = { ...current, ...patch }
      if (!next.lead_touched && ('schedule' in patch || 'interval_unit' in patch)) {
        next.lead_days = String(defaultLeadDays(next.schedule, next.interval_unit))
      }
      writeDraft(draftKey, next)
      return next
    })
  }

  const input = useMemo(() => toInput(draft), [draft])
  const hasParty = Boolean(input.organisation_id || input.person_id)

  useEffect(() => {
    if (!open || !hasParty) return
    const ticket = ++lookup.current
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      previewSchedule({ ...input, title: input.title || 'Voorbeeld', agreement_id: agreement?.id }, controller.signal)
        .then((answer) => {
          if (ticket !== lookup.current) return
          setPreview(answer)
          setProblem(null)
        })
        .catch((caught: unknown) => {
          if (ticket !== lookup.current || controller.signal.aborted) return
          setPreview(null)
          setProblem(caught instanceof CrmApiError || caught instanceof Error ? caught.message : 'Voorbeeld laden mislukt.')
        })
    }, PREVIEW_DELAY_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [open, hasParty, input, agreement?.id])

  async function save() {
    setSaving(true)
    try {
      const saved = agreement ? await updateAgreement(agreement.id, input) : await createAgreement(input)
      clearDraft(draftKey)
      toast.success(agreement ? 'Afspraak bijgewerkt.' : 'Afspraak vastgelegd.')
      onSaved(saved)
      onOpenChange(false)
    } catch (caught) {
      setProblem(caught instanceof Error ? caught.message : 'Opslaan mislukt.')
    } finally {
      setSaving(false)
    }
  }

  function cancel() {
    clearDraft(draftKey)
    onOpenChange(false)
  }

  const ownerName = membersById?.get(draft.owner)?.display_name ?? 'de eigenaar'
  const rhythm = draft.schedule === 'terugkerend' || draft.schedule === 'contactritme'
  const fixedAnchor = draft.schedule === 'eenmalig' || draft.schedule === 'terugkerend'
  const heading = agreement ? `Afspraak '${agreement.title}' bewerken` : party ? `Nieuwe afspraak met ${party.name}` : 'Nieuwe afspraak'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          <DialogDescription>Wat VONK met deze relatie afspreekt, wie erop let en hoe er taken uit ontstaan.</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            void save()
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${id}-title`}>Titel</Label>
            <Input id={`${id}-title`} value={draft.title} required maxLength={200} onChange={(event) => change({ title: event.target.value })} />
          </div>
          <CategorySelect label="Soort" scope="afspraak" value={draft.category} onChange={(category) => change({ category })} />
          {party ? null : (
            <div className="grid gap-3 md:grid-cols-2">
              <OrganisationCombobox
                value={draft.organisation ? { kind: 'existing', ...draft.organisation } : null}
                onChange={(choice) => change({ organisation: choice && choice.kind === 'existing' ? { id: choice.id, name: choice.name } : null })}
              />
              <ContactCombobox value={draft.person} onChange={(person) => change({ person })} />
            </div>
          )}
          {party?.kind === 'organisation' ? (
            <ContactCombobox label="Contactpersoon" value={draft.person} onChange={(person) => change({ person })} />
          ) : null}
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${id}-owner`}>Eigenaar</Label>
            <select id={`${id}-owner`} className={fieldClass} value={draft.owner} onChange={(event) => change({ owner: event.target.value })}>
              {[...(membersById?.values() ?? [])].map((member) => (
                <option key={member.id} value={member.id}>
                  {member.display_name}
                </option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">Krijgt de taken.</span>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${id}-start`}>Start</Label>
              <Input id={`${id}-start`} type="date" value={draft.starts_on} onChange={(event) => change({ starts_on: event.target.value })} />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${id}-end`}>Einde (optioneel)</Label>
              <Input id={`${id}-end`} type="date" value={draft.ends_on} onChange={(event) => change({ ends_on: event.target.value })} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span id={`${id}-description`} className="text-sm font-medium">
              Omschrijving
            </span>
            <RichTextEditor
              format="html"
              value={draft.description_html}
              onChange={(html) => change({ description_html: html })}
              ariaLabel="Omschrijving"
              ariaDescribedBy={`${id}-description`}
            />
          </div>
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">Opvolging</legend>
            <div role="radiogroup" aria-label="Opvolging" className="flex flex-wrap gap-2">
              {SCHEDULES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={draft.schedule === option.value}
                  onClick={() => change({ schedule: option.value })}
                  className={cn(
                    'rounded-full border px-3 py-1 text-sm max-md:min-h-11',
                    draft.schedule === option.value ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {rhythm ? (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {draft.schedule === 'contactritme' ? 'Minstens elke' : 'Elke'}
                <Input aria-label="Hoe vaak" type="number" min={1} max={99} className="w-16" value={draft.interval_count} onChange={(event) => change({ interval_count: event.target.value })} />
                <select aria-label="Eenheid" className={fieldClass} value={draft.interval_unit} onChange={(event) => change({ interval_unit: event.target.value as IntervalUnit })}>
                  {UNITS.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
                {draft.schedule === 'contactritme' ? 'contact' : null}
              </div>
            ) : null}
            {fixedAnchor ? (
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${id}-anchor`}>{draft.schedule === 'eenmalig' ? 'Deadline' : 'Eerste deadline'}</Label>
                <Input id={`${id}-anchor`} type="date" value={draft.anchor_on} onChange={(event) => change({ anchor_on: event.target.value })} />
              </div>
            ) : null}
            {draft.schedule === 'contactritme' ? (
              <p className="text-xs text-muted-foreground">Telt mee: gesprekken, meetings en e-mails (ook gesynchroniseerd).</p>
            ) : null}
            {draft.schedule !== 'geen' ? (
              <>
                <div className="flex items-center gap-2 text-sm">
                  Taak verschijnt
                  <Input aria-label="Dagen vooraf" type="number" min={0} max={120} className="w-16" value={draft.lead_days} onChange={(event) => change({ lead_days: event.target.value, lead_touched: true })} />
                  dagen vóór {draft.schedule === 'terugkerend' ? 'elke deadline' : 'de deadline'}
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`${id}-task-title`}>Taaktitel</Label>
                  <Input
                    id={`${id}-task-title`}
                    value={draft.task_title}
                    placeholder={[draft.title || 'Titel', draft.organisation?.name ?? draft.person?.name].filter(Boolean).join(' — ')}
                    onChange={(event) => change({ task_title: event.target.value })}
                  />
                </div>
              </>
            ) : null}
            {preview && preview.deadlines.length > 0 ? (
              <div className="flex flex-col gap-0.5 text-sm" aria-live="polite">
                {draft.schedule === 'contactritme' ? (
                  <span>
                    {preview.last_contact_on ? `Laatste contact: ${shortDay(preview.last_contact_on)} → ` : 'Nog geen contact → '}
                    uiterlijk {shortDay(preview.deadlines[0])}
                  </span>
                ) : (
                  <span>Volgende deadlines: {preview.deadlines.map((day) => shortDay(day)).join(' · ')}</span>
                )}
                {preview.task_from ? <span className="text-muted-foreground">Taak voor {ownerName} vanaf {shortDay(preview.task_from)}</span> : null}
                {preview.warnings.map((warning) => (
                  <span key={warning} className="text-muted-foreground">
                    {warning}
                  </span>
                ))}
              </div>
            ) : null}
          </fieldset>
          {draft.ends_on ? (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.remind} onChange={(event) => change({ remind: event.target.checked })} />
              Herinner
              <Input aria-label="Dagen vóór de einddatum" type="number" min={1} max={365} className="w-16" value={draft.end_reminder_days} onChange={(event) => change({ end_reminder_days: event.target.value })} />
              dagen vóór de einddatum (verlengen of beëindigen)
            </label>
          ) : null}
          {problem ? (
            <p role="alert" className="text-sm text-destructive">
              {problem}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={cancel}>
              Annuleren
            </Button>
            <Button type="submit" disabled={saving || !draft.title.trim() || !hasParty}>
              {saving ? 'Opslaan…' : 'Opslaan'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
